/**
 * Upload file directly to S3 using presigned URL
 * @param {File} file - The file to upload
 * @param {string} uploadUrl - The presigned URL from backend
 * @param {string} contentTypeOverride - Optional Content-Type to use (must match presign)
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<void>}
 */
export const uploadToS3 = async (file, uploadUrl, onProgress, contentTypeOverride) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percentComplete = (e.loaded / e.total) * 100;
        onProgress(Math.round(percentComplete));
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        // Try to parse error response
        let errorMessage = `Upload failed with status ${xhr.status}`;
        try {
          const errorText = xhr.responseText;
          if (errorText) {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(errorText, 'text/xml');
            const code = xmlDoc.getElementsByTagName('Code')[0]?.textContent;
            const message = xmlDoc.getElementsByTagName('Message')[0]?.textContent;
            if (code || message) {
              errorMessage = `${code || 'Error'}: ${message || errorMessage}`;
            }
          }
        } catch (e) {
          // If parsing fails, use default message
        }
        reject(new Error(errorMessage));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload. Please check your connection and S3 CORS configuration.'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    // IMPORTANT:
    // If the backend signs the presigned URL with a specific ContentType,
    // the PUT request must send the exact same Content-Type header.
    const contentType =
      contentTypeOverride ||
      file.type ||
      'application/octet-stream';

    // Open and send request
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
};

/**
 * Get file extension from filename
 * @param {string} filename
 * @returns {string}
 */
export const getFileExtension = (filename) => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

/**
 * Format file size for display
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file size in MB
 * @param {number} bytes
 * @returns {number}
 */
export const getFileSizeInMB = (bytes) => {
  return parseFloat((bytes / (1024 * 1024)).toFixed(2));
};
